<template>
  <error-boundary error-key="restrictionsPlacingVariants">
    <div class="d-flex justify-space-between">
      <create-restriction-button
        :operation="placingVariantsRestrictions?.operations.add"
        @click="setIsRestrictionDialogOpen(true)"
      />
      <v-spacer />
      <restrictions-table-toggle-button
        :active="isRestrictionsTableOpen"
        @click="setIsRestrictionsTableOpen(!isRestrictionsTableOpen)"
      />
    </div>

    <div class="d-lg-flex flex-lg-row-reverse mt-md-6">
      <restrictions-table
        v-model:open="isRestrictionsTableOpen"
        :period="placingVariantsRestrictionsChessPeriod"
        :table-headers="restrictionsTableHeaders"
        :table-items="filteredRestrictionsTableItems"
        :operation-delete="placingVariantsRestrictions?.operations.remove"
        @show="handleRestrictionShow"
        @edit="handleRestrictionEdit"
        @remove="handleRestrictionRemove"
        @open:chess="handleChessOpen"
      >
        <template v-slot:[`item.number`]="{ item }"> #{{ item.restriction.number }}</template>
        <template v-slot:[`item.room`]="{ item }">
          {{ item.restrictionFactor.roomCategory.name[0].value }} <br />
          {{ item.restrictionFactor.roomPlacing.placingVariant.name }}
        </template>
        <template v-slot:[`item.period`]="{ item }">
          {{ toShortDateFormat(item.period.from) }} <br />
          {{ toShortDateFormat(item.period.to) }}
        </template>
        <template v-slot:[`item.options`]="{ item }">
          {{ item.restriction.options?.isOpened ? 'Открыта' : 'Закрыта' }}<br />
          <template v-if="item.restriction.options?.isOpened">
            {{ item.restriction.options?.options?.isNeedConfirm ? 'Запрос' : 'Гарантия' }}
          </template>
          <template v-else>-</template>
        </template>
        <template #filters>
          <e-select
            v-model="filters.roomCategory"
            :items="roomCategoryItems"
            item-title="name[0].value"
            item-value="cid"
            label="Категория номера"
            clearable
            @update:model-value="filters.placingVariant = null"
          />
          <e-select
            v-model="filters.placingVariant"
            :items="placingVariantItems"
            item-title="restrictionFactor.placingVariant.name"
            item-value="restrictionFactor.cid"
            label="Вариант размещения"
            clearable
            :disabled="!filters.roomCategory"
          />
          <e-select
            v-model="filters.confirmationType"
            :items="confirmationTypeItems"
            label="Тип подтверждения"
            clearable
          />
        </template>
      </restrictions-table>
      <chess
        v-if="$vuetify.display.mdAndUp"
        ref="chessRef"
        :period="placingVariantsRestrictionsChessPeriod"
        :items="chessItems"
        :is-edit="
          isOperationEnabled(placingVariantsRestrictions?.operations.edit) ||
          isOperationEnabled(placingVariantsRestrictions?.operations.add)
        "
        :chess-dates-available-from="chessDatesAvailableFrom"
        :loading="isLoading"
        @select="handleChessSelect"
      >
        <template #tooltip="{ item }">
          <placing-variants-tooltip v-if="item.fixed.data" :placing-variant="item.fixed.data" />
        </template>
      </chess>
    </div>

    <placing-variants-restriction-dialog
      v-model="isRestrictionDialogOpen"
      :selected-cells="selectedChessCells"
      @close="chessRef?.unselectAllCells"
    />
    <remove-restriction-dialog
      v-model="isRemoveRestrictionDialogOpen"
      :number="selectedChessCells[0]?.rangeId"
      :action="removeRestrictionAction"
      :operation-delete="placingVariantsRestrictions?.operations.remove"
    />

    <e-dialog v-model="isChessCalendarDialogOpen">
      <template #title>
        <div class="caption">Ограничения ВР</div>
      </template>
      <e-select
        v-model="selectedTable"
        :items="tableList"
        label="Категория номера"
        @update:model-value="handleSelectedTableUpdate"
      />
      <e-select v-model="selectedRow" :items="rowList" label="Вариант размещения" class="mt-6" />
      <chess-calendar
        v-if="selectedTable && selectedRow"
        ref="chessCalendarRef"
        class="mt-6"
        :period="placingVariantsRestrictionsChessPeriod"
        :table="selectedTable"
        :cells="selectedRow.cells"
        :chess-dates-available-from="chessDatesAvailableFrom"
        :is-edit="
          isOperationEnabled(placingVariantsRestrictions?.operations.edit) ||
          isOperationEnabled(placingVariantsRestrictions?.operations.add)
        "
        @select="handleChessSelect"
      />
    </e-dialog>
  </error-boundary>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, nextTick, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import type {
  ChessCalendarComponent,
  ChessComponent,
  ChessDateCellType,
  ChessItemType,
  ChessTableRowCellsType,
  ChessTableRowType,
  ChessTableType,
} from '@/core/components/interface/Chess/Chess.type';
import type { ArrayElementType } from '@/core/types/gengeric';
import type { MultilanguageValue } from '@/core/utils/languages';
import type { RangeOfDateOnly } from '@/graphql/generated';
import type { PlacingVariantsRestrictionsCellDataType } from '@/parts/tariffs/store/restrictions';
import type { PlacingVariantSeatsType } from '@/parts/tariffs/types/tariffs';
import ChessCalendar from '@/core/components/interface/Chess/ChessCalendar.vue';
import Chess from '@/core/components/interface/Chess/index.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { ESelect } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import useToggle from '@/core/composables/useToggle';
import { getIsoDateWithUtcOffset, toShortDateFormat } from '@/core/utils/date';
import { isOperationEnabled } from '@/core/utils/permissions';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import PlacingVariantsTooltip from '@/parts/tariffs/components/PlacingVariantsTooltip.vue';
import CreateRestrictionButton from '@/parts/tariffs/components/PricesAndRestrictions/CreateRestrictionButton.vue';
import PlacingVariantsRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/PlacingVariants/PlacingVariantsRestrictionDialog.vue';
import RemoveRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/RemoveRestrictionDialog.vue';
import RestrictionsTable from '@/parts/tariffs/components/PricesAndRestrictions/RestrictionsTable.vue';
import RestrictionsTableToggleButton from '@/parts/tariffs/components/PricesAndRestrictions/RestrictionsTableToggleButton.vue';
import { useRestrictionsStore } from '@/parts/tariffs/store/restrictions';

const $route = useRoute();
const display = useDisplay();
const [isRestrictionsTableOpen, setIsRestrictionsTableOpen] = useToggle();
const [isRemoveRestrictionDialogOpen, setIsRemoveRestrictionDialogOpen] = useToggle();
const [isRestrictionDialogOpen, setIsRestrictionDialogOpen] = useToggle();
const [isChessCalendarDialogOpen, setIsChessCalendarDialogOpen] = useToggle();
const {
  placingVariantsRestrictions,
  placingVariantsRestrictionsChessPeriod,
  getPlacingVariantsRestrictions,
  removePlacingVariantsRestriction,
} = useRestrictionsStore();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();

const chessRef = ref<ChessComponent>();
const chessCalendarRef = ref<ChessCalendarComponent>();
const selectedChessCells = ref<ChessDateCellType<PlacingVariantsRestrictionsCellDataType>[]>([]);

const tariffScheduleCid = computed(() => String($route.params.t_id));

const chessItems = computed<ChessItemType<PlacingVariantsRestrictionsCellDataType, PlacingVariantSeatsType>[]>(() => {
  if (!placingVariantsRestrictions.value) return [];

  return [
    {
      tables: placingVariantsRestrictions.value.grid.map((gridItem) => {
        return {
          fixed: {
            title: `${gridItem.roomCategory.type.name} ${gridItem.roomCategory.name[0].value}`,
          },
          rows: gridItem.roomPlacings.map((roomPlacing) => {
            return {
              fixed: {
                title: roomPlacing.restrictionFactor.placingVariant.name,
                text: '<div class="mt-md-1">Статус продажи</div><div class="mt-md-05">Тип подтверждения</div>',
                data: roomPlacing.restrictionFactor.placingVariant,
              },
              cells: roomPlacing.restrictionPeriods.reduce<
                ChessTableRowCellsType<PlacingVariantsRestrictionsCellDataType>
              >((result, restrictionPeriod) => {
                return {
                  ...result,
                  ...getChessCellsFromGridItem(restrictionPeriod.period, {
                    roomCategory: gridItem.roomCategory.cid,
                    placingVariant: roomPlacing.restrictionFactor.cid,
                    restrictionId: restrictionPeriod.restrictionId,
                  }),
                };
              }, {}),
            };
          }),
          fixedWidth: 170,
          rowCellWidth: 84,
          cellHeight: display.xs.value ? 52 : 74,
        };
      }),
    },
  ];
});

const chessDatesAvailableFrom = computed(() => {
  return getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);
});

type PlacingVariantRestriction = {
  restriction: {
    number: number;
  };
  restrictionFactor: {
    roomCategory: {
      name: MultilanguageValue[];
    };
    roomPlacing: {
      placingVariant: {
        name: string;
      };
    };
  };
  period: RangeOfDateOnly;
};

const restrictionsTableHeaders = computed(() => [
  {
    key: 'number',
    title: '#',
    width: 48,
    sortRaw: (v1: PlacingVariantRestriction, v2: PlacingVariantRestriction) =>
      v2.restriction.number > v1.restriction.number ? -1 : 1,
  },
  {
    key: 'room',
    title: 'Номер',
    sortRaw: (v1: PlacingVariantRestriction, v2: PlacingVariantRestriction) =>
      `${v2.restrictionFactor.roomCategory.name[0]?.value} ${v2.restrictionFactor.roomPlacing.placingVariant.name}` >
      `${v1.restrictionFactor.roomCategory.name[0]?.value} ${v1.restrictionFactor.roomPlacing.placingVariant.name}`
        ? -1
        : 1,
  },
  {
    key: 'period',
    title: 'Период',
    sortRaw: (v1: PlacingVariantRestriction, v2: PlacingVariantRestriction) =>
      `${toShortDateFormat(v2.period.from)} ${toShortDateFormat(v2.period.to)}` >
      `${toShortDateFormat(v1.period.from)} ${toShortDateFormat(v1.period.to)}`
        ? -1
        : 1,
  },
  {
    key: 'options',
    title: 'Условие',
    sortable: false,
  },
  {
    key: 'actions',
    sortable: false,
    width: 40,
  },
]);

const restrictionsTableItems = computed(() => {
  return placingVariantsRestrictions.value?.table.map((item) => ({ ...item, period: item.restriction.period })) ?? [];
});

const filteredRestrictionsTableItems = computed(() => {
  return restrictionsTableItems.value.filter((item) => {
    return (
      (!filters.roomCategory || item.restrictionFactor.roomCategory.cid === filters.roomCategory) &&
      (!filters.placingVariant || item.restrictionFactor.roomPlacing.cid === filters.placingVariant) &&
      (filters.confirmationType === null ||
        item.restriction.options.options?.isNeedConfirm === filters.confirmationType)
    );
  });
});

//#region Фильтры
const filters = reactive({
  roomCategory: null,
  placingVariant: null,
  confirmationType: null,
});

const roomCategoryItems = computed(() => {
  return placingVariantsRestrictions.value?.grid.map((item) => item.roomCategory) ?? [];
});

const placingVariantItems = computed(() => {
  return (
    placingVariantsRestrictions.value?.grid.find((item) => item.roomCategory.cid === filters.roomCategory)
      ?.roomPlacings ?? []
  );
});

const confirmationTypeItems = [
  { title: 'Запрос', value: true },
  { title: 'Гарантия', value: false },
];
//#endregion Фильтры

//#region Mobile calendar
const selectedTable = ref<ChessTableType<PlacingVariantsRestrictionsCellDataType, PlacingVariantSeatsType>>();
const selectedRow = ref<ChessTableRowType<PlacingVariantsRestrictionsCellDataType, PlacingVariantSeatsType>>();

const tableList = computed(() =>
  chessItems.value[0].tables.map((item) => ({
    title: item.fixed?.title,
    value: item,
  })),
);

const rowList = computed(
  () =>
    selectedTable.value?.rows.map((item) => ({
      title: item.fixed?.title,
      value: item,
    })) ?? [],
);

const handleSelectedTableUpdate = () => {
  selectedRow.value = rowList.value[0].value;
};

const handleChessOpen = () => {
  selectedTable.value = chessItems.value[0].tables[0];
  selectedRow.value = selectedTable.value.rows[0];
  setIsChessCalendarDialogOpen(true);
};
//#endregion Mobile calendar

const handleRestrictionShow = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  if (!placingVariantsRestrictions.value) return;

  if (display.mdAndUp.value) {
    chessRef.value?.scrollTo(item.period.from);
  } else {
    const tableIndex = placingVariantsRestrictions.value.grid.findIndex((gridItem) => {
      return gridItem.roomCategory.cid === item.restrictionFactor.roomCategory.cid;
    });
    const rowIndex = placingVariantsRestrictions.value?.grid[tableIndex].roomPlacings.findIndex((roomPlacingItem) => {
      return roomPlacingItem.restrictionFactor.cid === item.restrictionFactor.roomPlacing.cid;
    });

    selectedTable.value = chessItems.value[0].tables[tableIndex];
    selectedRow.value = selectedTable.value.rows[rowIndex];

    setIsChessCalendarDialogOpen(true);
    nextTick(() => {
      chessCalendarRef.value?.scrollTo(item.period.from);
    });
  }
};

const handleRestrictionEdit = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  selectedChessCells.value = Object.values(
    getChessCellsFromGridItem(item.period, {
      roomCategory: item.restrictionFactor.roomCategory.cid,
      placingVariant: item.restrictionFactor.roomPlacing.cid,
      restrictionId: item.restriction.restrictionId,
    }),
  );
  setIsRestrictionDialogOpen(true);
};

const handleRestrictionRemove = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  selectedChessCells.value = Object.values(
    getChessCellsFromGridItem(item.period, {
      roomCategory: item.restrictionFactor.roomCategory.cid,
      placingVariant: item.restrictionFactor.roomPlacing.cid,
      restrictionId: item.restriction.restrictionId,
    }),
  );
  setIsRemoveRestrictionDialogOpen(true);
};

const handleChessSelect = async (cells: ChessDateCellType<PlacingVariantsRestrictionsCellDataType>[]) => {
  selectedChessCells.value = cells;

  if (cells.length) {
    setIsRestrictionDialogOpen(true);
  }
};

const removeRestrictionAction = async () => {
  if (!selectedChessCells.value[0]) return;

  await removePlacingVariantsRestriction({
    restrictionId: Number(selectedChessCells.value[0].data?.restrictionId),
    tariffScheduleCid: tariffScheduleCid.value,
  });
};

const getChessCellsFromGridItem = (period: RangeOfDateOnly, data: PlacingVariantsRestrictionsCellDataType) => {
  const result: ChessTableRowCellsType<PlacingVariantsRestrictionsCellDataType> = {};
  let options = placingVariantsRestrictions.value?.defaults.find(
    (item) =>
      item.restrictionFactorKey.roomCategoryCid === data.roomCategory &&
      item.restrictionFactorKey.roomPlacingCid === data.placingVariant,
  )?.defaultOptions;

  let rangeId;

  if (data.restrictionId) {
    const gridItemRestriction = placingVariantsRestrictions.value?.table.find(
      (item) => item.restriction.restrictionId === data.restrictionId,
    );

    if (gridItemRestriction) {
      options = gridItemRestriction.restriction.options;
      rangeId = gridItemRestriction.restriction.number;
    }
  }

  let periodDate = period.from;

  while (periodDate <= period.to) {
    result[periodDate] = {
      date: periodDate,
      content: `<div >${options?.isOpened ? 'Открыта' : 'Закрыта'}</div>
                <div class="mt-md-05">${options?.isOpened ? (options?.options?.isNeedConfirm ? 'Запрос' : 'Гарантия') : '-'}</div>`,
      rangeId,
      data,
    };
    periodDate = dayjs(periodDate).add(1, 'day').format('YYYY-MM-DD');
  }

  return result;
};

const { isLoading } = useFetch(async () => {
  await getPlacingVariantsRestrictions(tariffScheduleCid.value);
});
</script>
